import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Form, Input, Select, Typography, Tag, Tooltip } from 'antd';
import { Sample } from '../../../../../types/lab/SampleTypes';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { fetchTestCategories } from "../../../../../api/healthcare/Testing/TestCategories";
import { fetchTestTypesByCategory } from "../../../../../api/healthcare/Testing/TestTypes";
import { TestCategory, TestType } from '../../../../../types/healthcare/TestTypes';
import { SectionCard, StatusTag } from '../../../../../components/shared';

const { Text } = Typography;
const { TextArea } = Input;

interface LabAnalysisTabProps {
	sampleData: Sample;
	updateSampleData: (field: string, value: any) => void;
	updateMultipleFields: (updates: Record<string, any>) => void;
	getFieldError: (fieldName: string) => string | undefined;
	disabled: boolean;
}

const LabAnalysisTab: React.FC<LabAnalysisTabProps> = ({
	sampleData,
	updateSampleData,
	getFieldError,
	disabled,
}) => {
	const [testCategories, setTestCategories] = useState<TestCategory[]>([]);
	const [testTypes, setTestTypes] = useState<TestType[]>([]);
	const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("");

	useEffect(() => {
		const getCategories = async () => {
			try {
				const response = await fetchTestCategories({ include_test_types: false });
				if (response.success) setTestCategories(response.data.filter((cat) => cat.is_active));
			} catch (error) {
				console.log(error);
			}
		};
		getCategories();
	}, []);

	useEffect(() => {
		if (selectedCategoryFilter) {
			const fetchTestTypesForCategory = async () => {
				try {
					const response = await fetchTestTypesByCategory(selectedCategoryFilter, 1);
					if (response.success) setTestTypes(response.data.filter((type) => type.is_active));
				} catch (error) {
					console.log(error);
				}
			};
			fetchTestTypesForCategory();
		} else {
			setTestTypes([]);
		}
	}, [selectedCategoryFilter]);

	const handleCategoryFilterChange = (categoryValue: string) => {
		setSelectedCategoryFilter(categoryValue);
	};

	const analysisRequestedOptions = useMemo(() => {
		const optionsMap = new Map<string, { label: string; value: string }>();
		const currentlySelected = sampleData.sampleOA?.analysis_requested || [];
		currentlySelected.forEach((analysis) => {
			if (analysis && analysis.trim()) optionsMap.set(analysis, { label: analysis, value: analysis });
		});
		if (selectedCategoryFilter && testTypes.length > 0) {
			testTypes.forEach((testType) => optionsMap.set(testType.value, { label: testType.name, value: testType.value }));
		}
		return Array.from(optionsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
	}, [testTypes, selectedCategoryFilter, sampleData.sampleOA?.analysis_requested]);

	const categoryOptions = testCategories.map((cat) => ({ label: cat.name, value: cat.value }));
	const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500 };

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Analysis Configuration */}
			<SectionCard title="Analysis Configuration">
				<Row gutter={[16, 16]}>
					<Col span={12}>
						<Form.Item label={<Text style={labelStyle}>Filter by Test Category</Text>}>
							<Select
								value={selectedCategoryFilter || null}
								onChange={handleCategoryFilterChange}
								options={categoryOptions}
								disabled={disabled}
								showSearch
								allowClear
								size="small"
								placeholder="Select category to filter tests"
								filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
								suffixIcon={
									<Tooltip title="This filter helps you find tests. It is not saved to the sample." placement="topRight">
										<InfoCircleOutlined style={{ color: 'var(--mg-primary)', fontSize: 12 }} />
									</Tooltip>
								}
							/>
							<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block', marginTop: 4 }}>
								Optional: Select a category to filter available tests
							</Text>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label={<Text style={labelStyle}>Turnaround Time</Text>}>
							<Input
								value={sampleData.sampleOA?.turnaround || ""}
								onChange={(e) => updateSampleData("sampleOA.turnaround", e.target.value)}
								disabled={disabled}
								size="small"
								placeholder="Expected turnaround time"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Form.Item
							label={<Text style={labelStyle}>Analysis Requested <span style={{ color: 'var(--mg-danger)' }}>*</span></Text>}
							help={getFieldError("sampleOA.analysis_requested")}
							validateStatus={getFieldError("sampleOA.analysis_requested") ? 'error' : ''}
						>
							<Select
								mode="multiple"
								value={sampleData.sampleOA?.analysis_requested || []}
								onChange={(value) => updateSampleData("sampleOA.analysis_requested", value)}
								options={analysisRequestedOptions}
								maxTagCount="responsive"
								showSearch
								disabled={disabled}
								size="small"
								placeholder="Select analysis types"
								filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
								notFoundContent={selectedCategoryFilter ? "No tests available for selected category" : "Select a category above to see available tests"}
							/>
							<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block', marginTop: 4 }}>
								{selectedCategoryFilter
									? `Showing tests from ${testCategories.find((cat) => cat.value === selectedCategoryFilter)?.name || "selected category"} and any already selected`
									: "Select a category above for specific tests, or choose from common options"}
							</Text>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			{/* Delivery & Reporting */}
			<SectionCard title="Delivery & Reporting">
				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Delivery Method</Text>}>
							<Select
								value={sampleData.sampleOA?.delivery}
								onChange={(value) => updateSampleData("sampleOA.delivery", value)}
								disabled={disabled}
								size="small"
								placeholder="Delivery method"
								options={[
									{ label: "Email", value: "email" },
									{ label: "Portal", value: "portal" },
									{ label: "Postal Mail", value: "mail" },
									{ label: "Courier", value: "courier" },
									{ label: "Digital Download", value: "download" },
								]}
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Tracking Number</Text>}>
							<Input
								value={sampleData.sampleOA?.tracking_number || ""}
								onChange={(e) => updateSampleData("sampleOA.tracking_number", e.target.value)}
								disabled={disabled}
								size="small"
								placeholder="Delivery tracking number"
							/>
						</Form.Item>
					</Col>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Delivery Vendor</Text>}>
							<Input
								value={sampleData.sampleOA?.delivery_vendor || ""}
								onChange={(e) => updateSampleData("sampleOA.delivery_vendor", e.target.value)}
								disabled={disabled}
								size="small"
								placeholder="Courier/vendor name"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Form.Item label={<Text style={labelStyle}>Reported</Text>}>
							<Select
								value={sampleData.sampleOA?.reported}
								onChange={(value) => updateSampleData("sampleOA.reported", value)}
								disabled={disabled}
								size="small"
								placeholder="Report status"
								options={[
									{ label: "Not Reported", value: false },
									{ label: "Reported", value: true },
								]}
							/>
						</Form.Item>
					</Col>
					<Col span={16}>
						<Form.Item label={<Text style={labelStyle}>Recollect Notice</Text>}>
							<TextArea
								value={sampleData.sampleOA?.recollect_notice || ""}
								onChange={(e) => updateSampleData("sampleOA.recollect_notice", e.target.value)}
								disabled={disabled}
								size="small"
								placeholder="Recollection requirements or notices"
								rows={3}
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			{/* Analysis Status Summary */}
			{sampleData.sampleOA && (
				<SectionCard title="Analysis Status Summary">
					<Row gutter={[16, 16]}>
						<Col span={12}>
							<Text style={{ fontSize: 12, fontWeight: 500 }}>Analysis Progress:</Text>
							<div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Text style={{ fontSize: 12 }}>Status:</Text>
									<StatusTag status={sampleData.sampleOA.status || 'pending'} />
								</div>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Text style={{ fontSize: 12 }}>Reported:</Text>
									{sampleData.sampleOA.reported
										? <CheckCircleOutlined style={{ color: 'var(--mg-success)' }} />
										: <CloseCircleOutlined style={{ color: 'var(--mg-text-disabled)' }} />}
								</div>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Text style={{ fontSize: 12 }}>Analyses:</Text>
									<Text style={{ fontSize: 12 }}>{sampleData.sampleOA.analysis_requested?.length || 0} requested</Text>
								</div>
							</div>
						</Col>
					</Row>
				</SectionCard>
			)}
		</div>
	);
};

export default LabAnalysisTab;
